/**
 * Professionals — one public page serving /therapist-portal and
 * /schools-portal (replaces the auth-gated ComingSoonPage stubs; contest
 * brief, 2026-07-11).
 *
 * Law honored (blueprint R4-C/R4-G, TT-1, Part I):
 *   - Professionals never pay for SonSpeak. Ever.
 *   - Connections are invite-only, initiated by the family. Canonical copy:
 *     "Caregiver-confirmed, invite-only professionals you know." The word
 *     "verified" never appears (there is no verification program yet, and
 *     honest gating applies to labels too).
 *   - Schools: one council-crafted classroom board per student at launch,
 *     classroom-scoped; the home board is untouched and the parent holds
 *     both keys.
 */
import { Link, useLocation } from "react-router-dom";
import { Stethoscope, School, QrCode, MessageSquare, ShieldCheck, Mail } from "lucide-react";
import { useSEO } from "@/lib/seo";

const FLOW = [
  {
    Icon: QrCode,
    title: "The family invites you",
    body:
      "A parent generates a single-use invite from their app — in the room " +
      "or by link. Connections always start with the family. Caregiver-" +
      "confirmed, invite-only professionals you know.",
  },
  {
    Icon: ShieldCheck,
    title: "You see structure, never private media",
    body:
      "Shared boards show categories, words, and layout. Family photos and " +
      "voice recordings stay on the family's device — your view says " +
      "“family photo — on family device.”",
  },
  {
    Icon: MessageSquare,
    title: "You suggest, the parent approves",
    body:
      "Add a tile, reword one, retire one, or leave a note. Every change " +
      "lands as a suggestion the caregiver approves or declines — nothing " +
      "edits a child's board but the family.",
  },
];

export default function Professionals() {
  const { pathname } = useLocation();
  const isSchool = pathname === "/schools-portal";

  useSEO({
    title: isSchool ? "For schools" : "For therapists & clinicians",
    description:
      "SonSpeak never charges professionals. Families invite you; you see " +
      "board structure, suggest changes, and the parent approves. Schools " +
      "get a classroom-scoped board per student at launch.",
    path: pathname,
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="professionals-page">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8 text-center">
        <p className="display text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] mb-3">
          {isSchool ? "For schools & programs" : "For therapists & clinicians"}
        </p>
        <h1 className="display text-3xl sm:text-5xl font-extrabold leading-tight mb-4">
          You never pay. <span className="text-[color:var(--coral)]">Families stay in charge.</span>
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 max-w-2xl mx-auto leading-relaxed">
          SonSpeak doesn&rsquo;t charge professionals — not per seat, not per
          student, not ever. Your access comes from one place: a family
          inviting you to help with their child&rsquo;s board.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-8" aria-label="How professional connections work">
        <div className="grid sm:grid-cols-3 gap-4">
          {FLOW.map(({ Icon, title, body }) => (
            <div key={title} className="brutal-card bg-white p-6">
              <span className="inline-flex w-10 h-10 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] items-center justify-center mb-3">
                <Icon size={18} aria-hidden />
              </span>
              <h2 className="display font-extrabold mb-2">{title}</h2>
              <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {isSchool ? (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8">
          <div className="brutal-card bg-[color:var(--paper)] p-6">
            <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
              <School size={20} aria-hidden /> A classroom board, not a smaller voice
            </h2>
            <p className="text-sm leading-relaxed mb-2">
              At launch, a school account receives one professionally-composed,
              classroom-scoped board per student — built for the school
              context, at no cost to the school or the family.
            </p>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              School mode curates a <em>context</em>; it never shrinks the
              child&rsquo;s <em>voice</em>. The full home board is untouched, and
              the parent holds both keys: pairing permission and change
              approval.
            </p>
          </div>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8">
          <div className="brutal-card bg-[color:var(--paper)] p-6">
            <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
              <Stethoscope size={20} aria-hidden /> Built for the 15-minute visit
            </h2>
            <p className="text-sm leading-relaxed mb-2">
              Scan the family&rsquo;s invite in the room, then review and suggest
              on your own time. Usage data appears only when the caregiver
              chooses to share it — what&rsquo;s being tapped, what&rsquo;s being
              ignored, where to encourage another try.
            </p>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              Want families to find you meanwhile? The{" "}
              <Link to="/therapists" className="underline font-semibold">clinician directory</Link>{" "}
              lists hand-reviewed profiles.
            </p>
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
        <div className="brutal-card bg-[color:var(--gold)] p-6 text-center">
          <h2 className="display text-lg font-extrabold mb-2 flex items-center justify-center gap-2">
            <Mail size={18} aria-hidden /> Bring SonSpeak to your {isSchool ? "school" : "practice"}
          </h2>
          <p className="text-sm leading-relaxed mb-4 max-w-xl mx-auto">
            Professional connections ship with the full app launch. Write to
            me now and I&rsquo;ll set you up the moment they open — and answer
            anything about how the privacy model affects your workflow.
          </p>
          <a className="pill-btn primary" href="mailto:hello@sonspeak.com" data-testid="professionals-contact">
            hello@sonspeak.com
          </a>
        </div>
      </section>
    </div>
  );
}
